/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { createContext, useState, useRef, useCallback, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { InlineLoading, Modal as Modal$1, TextInput } from '@carbon/react';
import { useTranslation } from '../../../../Utilities/i18n/i18n.js';
import ErrorModal from '../../ErrorModal/ErrorModal.js';

var _excluded = ["title", "label", "subtitle", "text", "caption", "status", "error", "recovery", "onClose"],
  _excluded2 = ["title", "label", "content", "onClose"],
  _excluded3 = ["title", "label", "content", "buttonText", "secondaryButtonText", "onClose", "onSubmit", "disabled", "danger", "matchText", "matchTextLabel", "showSubmitSpinner"],
  _excluded4 = ["content", "matchText", "matchTextLabel", "disabled"];
var Context = /*#__PURE__*/createContext();
Context.displayName = 'ModalContext';
var Provider = function Provider(_ref) {
  var children = _ref.children,
    hideFromDom = _ref.hideFromDom;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    modalProps = _useState2[0],
    setModalProps = _useState2[1];
  var _useState3 = useState(),
    _useState4 = _slicedToArray(_useState3, 2),
    errorModalProps = _useState4[0],
    setErrorModalProps = _useState4[1];
  var _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    matchTextValue = _useState6[0],
    setMatchTextValue = _useState6[1];
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var resetTimeout = useRef();
  var reset = useCallback(function () {
    setModalProps(function (prev) {
      return _objectSpread2(_objectSpread2({}, prev), {}, {
        open: false
      });
    });
    setErrorModalProps(function (prev) {
      return _objectSpread2(_objectSpread2({}, prev), {}, {
        open: false
      });
    });

    // Let the Modal animate out before clearing all the props
    clearTimeout(resetTimeout.current);
    resetTimeout.current = setTimeout(function () {
      setModalProps();
      setErrorModalProps();
      setMatchTextValue('');
    }, 500);
  }, []);
  var error = useCallback(function (_ref2) {
    var title = _ref2.title,
      label = _ref2.label,
      subtitle = _ref2.subtitle,
      text = _ref2.text,
      caption = _ref2.caption,
      status = _ref2.status,
      errorObject = _ref2.error,
      recovery = _ref2.recovery,
      onClose = _ref2.onClose,
      rest = _objectWithoutProperties(_ref2, _excluded);
    clearTimeout(resetTimeout.current);
    var close = function close() {
      if (onClose) onClose();
      reset();
    };
    setErrorModalProps(_objectSpread2({
      title: title,
      label: label,
      subtitle: subtitle,
      text: text,
      caption: caption,
      status: status,
      error: errorObject,
      recovery: recovery,
      onClose: close,
      open: true
    }, rest));
    setModalProps();
  }, [reset]);
  var info = useCallback(function (_ref3) {
    var title = _ref3.title,
      label = _ref3.label,
      content = _ref3.content,
      onClose = _ref3.onClose,
      rest = _objectWithoutProperties(_ref3, _excluded2);
    clearTimeout(resetTimeout.current);
    var close = function close() {
      if (onClose) onClose();
      reset();
    };
    setModalProps(_objectSpread2({
      modalHeading: title,
      'aria-label': title,
      modalLabel: label,
      onRequestClose: close,
      onRequestSubmit: close,
      content: content,
      passiveModal: true,
      open: true
    }, rest));
    setErrorModalProps();
  }, [reset]);
  var confirm = useCallback(function (_ref4) {
    var title = _ref4.title,
      label = _ref4.label,
      content = _ref4.content,
      buttonText = _ref4.buttonText,
      secondaryButtonText = _ref4.secondaryButtonText,
      onClose = _ref4.onClose,
      onSubmit = _ref4.onSubmit,
      disabled = _ref4.disabled,
      danger = _ref4.danger,
      matchText = _ref4.matchText,
      matchTextLabel = _ref4.matchTextLabel,
      showSubmitSpinner = _ref4.showSubmitSpinner,
      rest = _objectWithoutProperties(_ref4, _excluded3);
    clearTimeout(resetTimeout.current);
    var close = function close() {
      if (onClose) onClose();
      reset();
    };
    var submit = function submit() {
      if (onClose) onClose();
      if (onSubmit) onSubmit();
      reset();
    };
    setModalProps(_objectSpread2({
      modalHeading: title,
      'aria-label': title,
      modalLabel: label,
      onRequestClose: close,
      onRequestSubmit: submit,
      secondaryButtonText: secondaryButtonText || t('Cancel'),
      primaryButtonText: showSubmitSpinner ? /*#__PURE__*/React.createElement(InlineLoading, {
        status: "active",
        description: buttonText || t('Submit')
      }) : buttonText || t('Submit'),
      danger: danger,
      content: content,
      matchText: matchText,
      matchTextLabel: matchTextLabel,
      disabled: disabled,
      open: true
    }, rest));
    setErrorModalProps();
  }, [t, reset]);
  var update = useCallback(function (props) {
    setModalProps(function (prev) {
      return _objectSpread2(_objectSpread2({}, prev), props);
    });
  }, []);
  var value = useMemo(function () {
    return {
      error: error,
      info: info,
      confirm: confirm,
      update: update,
      reset: reset
    };
  }, [error, info, confirm, update, reset]);
  var _ref5 = modalProps || {},
    content = _ref5.content,
    matchText = _ref5.matchText,
    matchTextLabel = _ref5.matchTextLabel,
    disabled = _ref5.disabled,
    other = _objectWithoutProperties(_ref5, _excluded4);
  return /*#__PURE__*/React.createElement(Context.Provider, {
    value: value
  }, (!!hideFromDom && (errorModalProps === null || errorModalProps === void 0 ? void 0 : errorModalProps.open) || !hideFromDom) && /*#__PURE__*/React.createElement(ErrorModal, errorModalProps), (!!hideFromDom && (modalProps === null || modalProps === void 0 ? void 0 : modalProps.open) || !hideFromDom) && /*#__PURE__*/React.createElement(Modal$1, _extends({}, other, {
    primaryButtonDisabled: disabled || !!matchText && matchTextValue !== matchText
  }), content, !!matchText && /*#__PURE__*/React.createElement("div", {
    style: {
      marginTop: '1.5rem'
    }
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: matchTextValue,
    placeholder: matchText,
    labelText: matchTextLabel || t('Type "{{name}}" to confirm', {
      name: matchText
    }),
    id: "cpc--provider-modal-match-text",
    onChange: function onChange(evt) {
      return setMatchTextValue(evt.target.value);
    }
  }))), children);
};
Provider.propTypes = {
  children: PropTypes.node.isRequired,
  hideFromDom: PropTypes.bool
};
var useModal = function useModal() {
  return useContext(Context);
};
var Modal = {
  Context: Context,
  Provider: Provider,
  useModal: useModal
};

export { Modal as default };
